import { useState, useEffect } from "react";
import {
  Drawer,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  TextInput,
  Select,
  NumberInput,
  Paper,
  ThemeIcon,
  Alert,
  Loader,
  Textarea,
  Tabs,
  Center,
  Anchor,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { DatePicker } from "@mantine/dates";
import {
  IconSparkles,
  IconFileText,
  IconCalendar,
  IconExternalLink,
  IconAlertCircle,
  IconPlayerSkipForward,
  IconEye,
  IconSettings,
  IconNotes,
  IconBook,
} from "@tabler/icons-react";
import { useContentPlansStore } from "./contentPlansStore";
import { mapGenerationError } from "./generationError";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  getContentItem,
  publishContentItem,
  reviewContentItem,
} from "../Content/contentApi";
import { fetchDossierForWorkstream } from "./researchApi";
import { kindColor, hostOf } from "./ResearchPanel";
import { useBrandContext } from "../Brands/brandContext";

const statusColors = {
  idea: "gray",
  approvedForGeneration: "green",
  generating: "blue",
  draftReadyForReview: "orange",
  published: "teal",
  rejected: "red",
  archived: "gray",
  skipped: "gray",
};

const statusLabels = {
  idea: "Idea",
  approvedForGeneration: "Approved",
  generating: "Generating...",
  draftReadyForReview: "Draft Ready",
  published: "Published",
  rejected: "Rejected",
  archived: "Archived",
  skipped: "Skipped",
};

export default function ContentItemDetailDrawer({
  contentItem,
  workstreamTitle,
  planId,
  workstreamId,
  hasResearchedDossier,
  opened,
  onClose,
}) {
  const { patchContentItem, generateContentItem, generateBrief, fetchContentPlan } =
    useContentPlansStore();
  const { brand } = useBrandContext();
  const autopilot = brand?.autopilot;
  const [isEditing, setIsEditing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);
  const [generationNotes, setGenerationNotes] = useState("");
  const [contentDoc, setContentDoc] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [dossier, setDossier] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [reviewing, setReviewing] = useState(false);

  // Fetch content document for preview
  useEffect(() => {
    if (opened && contentItem?.contentDocumentId) {
      setLoadingContent(true);
      getContentItem(contentItem.contentDocumentId)
        .then(setContentDoc)
        .catch(() => setContentDoc(null))
        .finally(() => setLoadingContent(false));
    } else {
      setContentDoc(null);
    }
  }, [opened, contentItem?.contentDocumentId]);

  useEffect(() => {
    if (!opened || !planId || !workstreamId) {
      setDossier(null);
      return undefined;
    }
    let cancelled = false;
    fetchDossierForWorkstream(planId, workstreamId).then((d) => {
      if (!cancelled) setDossier(d);
    });
    return () => {
      cancelled = true;
    };
  }, [opened, planId, workstreamId]);

  const [brief, setBrief] = useState("");
  const [editData, setEditData] = useState({
    title: "",
    primaryKeyword: "",
    targetWordCount: 1000,
    contentType: "blogPost",
    contentRole: "support",
    scheduledDate: null,
  });

  useEffect(() => {
    if (contentItem) {
      setEditData({
        title: contentItem.title || "",
        primaryKeyword: contentItem.primaryKeyword || "",
        targetWordCount: contentItem.targetWordCount || 1000,
        contentType: contentItem.contentType || "blogPost",
        contentRole: contentItem.contentRole || "support",
        scheduledDate: contentItem.scheduledDate
          ? new Date(contentItem.scheduledDate)
          : null,
      });
      setGenerationNotes(contentItem.generationNotes || "");
      setBrief(contentItem.brief || "");
    }
  }, [contentItem]);

  if (!contentItem) return null;

  const handleSkip = async () => {
    await patchContentItem(planId, workstreamId, contentItem.id, {
      status: "skipped",
    });
    onClose();
  };

  const handleSaveGenerationNotes = async () => {
    await patchContentItem(planId, workstreamId, contentItem.id, {
      generationNotes: generationNotes,
    });
  };

  const handleGenerateBrief = async () => {
    setIsGeneratingBrief(true);
    try {
      const result = await generateBrief(planId, workstreamId, contentItem.id);
      if (result?.brief) setBrief(result.brief);
    } finally {
      setIsGeneratingBrief(false);
    }
  };

  const handleSaveBrief = async () => {
    await patchContentItem(planId, workstreamId, contentItem.id, {
      brief: brief,
    });
  };

  const handleWrite = async () => {
    setIsGenerating(true);
    try {
      if (contentItem.status === "idea") {
        const updated = await patchContentItem(planId, workstreamId, contentItem.id, {
          status: "approvedForGeneration",
        });
        if (!updated) return;
      }
      await generateContentItem(planId, workstreamId, contentItem.id);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    await patchContentItem(planId, workstreamId, contentItem.id, {
      ...editData,
      scheduledDate: editData.scheduledDate?.toISOString() || null,
    });
    setIsEditing(false);
  };

  const handleMarkReviewed = async () => {
    if (!contentItem.contentDocumentId) return;
    setReviewing(true);
    try {
      const updated = await reviewContentItem(contentItem.contentDocumentId);
      setContentDoc(updated);
      notifications.show({
        title: "Reviewed",
        message: "Review stamp saved. Cluster publish can include this article.",
        color: "teal",
      });
    } catch {
      // api.js already surfaced the error
    } finally {
      setReviewing(false);
    }
  };

  const handleReviewedPublish = async () => {
    if (!contentItem.contentDocumentId) return;
    setPublishing(true);
    try {
      const updated = await publishContentItem(contentItem.contentDocumentId);
      setContentDoc(updated);
      if (planId) await fetchContentPlan(planId);
      notifications.show({
        title: "Published",
        message: "Article is live. Review stamp saved.",
        color: "teal",
      });
      onClose();
    } catch {
      // api.js already surfaced the error
    } finally {
      setPublishing(false);
    }
  };

  const handleScheduleChange = async (date) => {
    setEditData({ ...editData, scheduledDate: date });
    if (!isEditing) {
      await patchContentItem(planId, workstreamId, contentItem.id, {
        scheduledDate: date?.toISOString() || null,
      });
    }
  };

  const isPillar = contentItem.contentRole?.toLowerCase() === "pillar";
  const hasContent = contentItem.contentDocumentId != null;
  const isPreGeneration = contentItem.status === "idea" || contentItem.status === "approvedForGeneration";
  const isReadyForReview = contentItem.status === "draftReadyForReview";
  const errorText = mapGenerationError(contentItem.lastError);
  const claimIds = contentDoc?.researchClaimIds;
  const usedClaims =
    dossier?.claims && claimIds?.length
      ? dossier.claims.filter((c) => claimIds.includes(c.id))
      : [];
  const showSourceFallback = hasContent && !claimIds?.length;
  const isPublished =
    contentDoc?.status === "Published" || contentItem.status === "published";

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Group>
          <ThemeIcon
            size="lg"
            variant="light"
            color={isPillar ? "violet" : "cyan"}
          >
            <IconFileText size={20} />
          </ThemeIcon>
          <div>
            <Text fw={600} size="lg" lineClamp={1}>
              {contentItem.title}
            </Text>
            <Group gap="xs">
              <Badge
                color={statusColors[contentItem.status] || "gray"}
                variant="light"
                size="sm"
              >
                {statusLabels[contentItem.status] || contentItem.status}
              </Badge>
              <Badge color={isPillar ? "violet" : "cyan"} variant="filled" size="sm">
                {isPillar ? "Pillar" : "Support"}
              </Badge>
              {contentItem.scheduledDate && autopilot?.enabled && (
                <Text size="xs" c={!autopilot.requireApprovalBeforePublish ? "green" : "dimmed"}>
                  {!autopilot.requireApprovalBeforePublish
                    ? `Auto-publish: ${new Date(contentItem.scheduledDate).toLocaleDateString()}`
                    : "Manual review required"}
                </Text>
              )}
            </Group>
          </div>
        </Group>
      }
      position="right"
      size="lg"
      padding="xl"
    >
      <Tabs
        key={`${contentItem.id}-${opened}`}
        defaultValue={isReadyForReview && hasContent ? "claims" : "content"}
      >
        <Tabs.List mb="md">
          <Tabs.Tab value="content" leftSection={<IconEye size={14} />}>
            Content
          </Tabs.Tab>
          {hasContent && (
            <Tabs.Tab value="claims" leftSection={<IconBook size={14} />}>
              Claims
            </Tabs.Tab>
          )}
          <Tabs.Tab value="details" leftSection={<IconSettings size={14} />}>
            Details
          </Tabs.Tab>
          {isPreGeneration && (
            <Tabs.Tab value="brief" leftSection={<IconNotes size={14} />}>
              Notes
            </Tabs.Tab>
          )}
        </Tabs.List>

        {/* ========== CONTENT TAB ========== */}
        <Tabs.Panel value="content">
          <Stack gap="md">
            {/* Action alerts */}
            {errorText && (
              <Alert icon={<IconAlertCircle size={16} />} title="Could not write this article" color="red" variant="light">
                <Text size="sm">{errorText}</Text>
              </Alert>
            )}

            {isPreGeneration && (
              <Alert
                icon={<IconSparkles size={16} />}
                title={
                  hasResearchedDossier
                    ? "Write from this cluster's research"
                    : "Research this cluster first"
                }
                color={hasResearchedDossier ? "blue" : "orange"}
                variant="light"
              >
                <Stack gap="xs">
                  <Text size="sm">
                    {hasResearchedDossier
                      ? "A brief is not required. The dossier is the specification."
                      : "Articles are written from a researched dossier. Research this cluster first."}
                  </Text>
                  <Group>
                    <Button
                      size="xs"
                      color="blue"
                      leftSection={<IconSparkles size={14} />}
                      onClick={handleWrite}
                      loading={isGenerating}
                      disabled={!hasResearchedDossier}
                    >
                      {errorText ? "Retry" : "Write"}
                    </Button>
                    <Button
                      size="xs"
                      color="gray"
                      variant="outline"
                      leftSection={<IconPlayerSkipForward size={14} />}
                      onClick={handleSkip}
                    >
                      Skip
                    </Button>
                  </Group>
                </Stack>
              </Alert>
            )}

            {contentItem.status === "generating" && (
              <Alert icon={<Loader size={16} />} title="Generating Content" color="blue" variant="light">
                <Text size="sm">Content is being generated. This may take a minute...</Text>
              </Alert>
            )}

            {/* Content preview */}
            {hasContent && (
              <>
                <Group justify="space-between">
                  <Text size="sm" fw={600}>Content Preview</Text>
                  <Button
                    size="xs"
                    variant="light"
                    component="a"
                    href={`/content/${contentItem.contentDocumentId}`}
                    leftSection={<IconExternalLink size={14} />}
                  >
                    Open Editor
                  </Button>
                </Group>
                <Paper p="md" withBorder style={{ maxHeight: 500, overflow: "auto" }} className="markdown">
                  {loadingContent ? (
                    <Center h={100}><Loader size="sm" /></Center>
                  ) : contentDoc?.content ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {contentDoc.content}
                    </ReactMarkdown>
                  ) : (
                    <Text c="dimmed" fs="italic" ta="center">No content yet</Text>
                  )}
                </Paper>
              </>
            )}

            {!hasContent && !isPreGeneration && contentItem.status !== "generating" && (
              <Center h={100}>
                <Text c="dimmed" fs="italic">No content generated yet</Text>
              </Center>
            )}
          </Stack>
        </Tabs.Panel>

        {/* ========== CLAIMS TAB ========== */}
        {hasContent && (
          <Tabs.Panel value="claims">
            <Stack gap="md">
              {loadingContent ? (
                <Center h={80}>
                  <Loader size="sm" />
                </Center>
              ) : (
                <>
                  {usedClaims.length > 0 && (
                    <Stack gap="xs">
                      <Text size="sm" fw={600}>
                        Claims this draft was written from
                      </Text>
                      {usedClaims.map((c) => (
                        <Paper
                          key={c.id}
                          p="xs"
                          withBorder
                          bg={c.kind?.toLowerCase() === "primary" ? "grape.0" : undefined}
                        >
                          <Group gap="xs" mb={4} wrap="nowrap">
                            <Badge
                              size="xs"
                              color={kindColor[c.kind] || "blue"}
                              variant="light"
                            >
                              {c.kind}
                            </Badge>
                            {c.isMultiSource && (
                              <Badge size="xs" color="teal" variant="light">
                                Multi-source
                              </Badge>
                            )}
                          </Group>
                          <Text size="sm">{c.text}</Text>
                          <Group gap={6} mt={4}>
                            {(c.sourceUrls || []).map((url) => (
                              <Anchor
                                key={url}
                                href={url}
                                target="_blank"
                                rel="noreferrer noopener"
                                size="xs"
                              >
                                {hostOf(url)}
                                <IconExternalLink
                                  size={10}
                                  style={{ marginLeft: 3, verticalAlign: "middle" }}
                                />
                              </Anchor>
                            ))}
                          </Group>
                        </Paper>
                      ))}
                    </Stack>
                  )}

                  {showSourceFallback && (
                    <Stack gap="xs">
                      <Text size="sm" fw={600}>
                        Sources the writer saw
                      </Text>
                      <Text size="xs" c="dimmed">
                        This draft has no claim ids. These are the sources the writer
                        was given.
                      </Text>
                      {(contentDoc?.researchSources || []).length === 0 ? (
                        <Text size="sm" c="dimmed" fs="italic">
                          No structured sources on this article.
                        </Text>
                      ) : (
                        (contentDoc.researchSources || []).map((s) => (
                          <Paper key={s.url} p="xs" withBorder>
                            <Anchor
                              href={s.url}
                              target="_blank"
                              rel="noreferrer noopener"
                              size="sm"
                            >
                              {s.title || s.url}
                            </Anchor>
                            <Text size="xs" c="dimmed">
                              {s.domain || hostOf(s.url)}
                            </Text>
                          </Paper>
                        ))
                      )}
                    </Stack>
                  )}

                  {!showSourceFallback && usedClaims.length === 0 && dossier && (
                    <Text size="sm" c="dimmed" fs="italic">
                      No matching claims on the cluster dossier.
                    </Text>
                  )}

                  {contentDoc?.reviewedAt && (
                    <Text size="xs" c="dimmed">
                      Reviewed{" "}
                      {new Date(contentDoc.reviewedAt).toLocaleString()}
                      {contentDoc.reviewedBy
                        ? ` · ${contentDoc.reviewedBy}`
                        : ""}
                    </Text>
                  )}

                  {isReadyForReview && !isPublished && (
                    <Group>
                      <Button
                        variant="light"
                        onClick={handleMarkReviewed}
                        loading={reviewing}
                        disabled={!!contentDoc?.reviewedAt}
                      >
                        Mark as reviewed
                      </Button>
                      <Button
                        color="teal"
                        onClick={handleReviewedPublish}
                        loading={publishing}
                      >
                        Reviewed — publish
                      </Button>
                    </Group>
                  )}
                </>
              )}
            </Stack>
          </Tabs.Panel>
        )}

        {/* ========== DETAILS TAB ========== */}
        <Tabs.Panel value="details">
          {isEditing ? (
            <Stack gap="md">
              <TextInput label="Title" value={editData.title} onChange={(e) => setEditData({ ...editData, title: e.target.value })} />
              <TextInput label="Primary Keyword" value={editData.primaryKeyword} onChange={(e) => setEditData({ ...editData, primaryKeyword: e.target.value })} />
              <NumberInput label="Target Word Count" value={editData.targetWordCount} onChange={(value) => setEditData({ ...editData, targetWordCount: value })} min={500} max={10000} step={500} />
              <Select label="Content Type" value={editData.contentType} onChange={(value) => setEditData({ ...editData, contentType: value })} data={[{ value: "blogPost", label: "Blog Post" }, { value: "programmaticSeoPage", label: "pSEO Page" }, { value: "landingPage", label: "Landing Page" }]} />
              <Select label="Content Role" value={editData.contentRole?.toLowerCase()} onChange={(value) => setEditData({ ...editData, contentRole: value })} data={[{ value: "pillar", label: "Pillar (Comprehensive)" }, { value: "support", label: "Support (Focused)" }]} />
              <DatePicker label="Scheduled Date" value={editData.scheduledDate} onChange={(date) => setEditData({ ...editData, scheduledDate: date })} clearable />
              <Group>
                <Button onClick={handleSave}>Save Changes</Button>
                <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
              </Group>
            </Stack>
          ) : (
            <Stack gap="md">
              <Paper p="xs" withBorder>
                <Text size="sm" c="dimmed">
                  Part of cluster: <Text span fw={500}>{workstreamTitle}</Text>
                </Text>
              </Paper>

              <div>
                <Text size="sm" c="dimmed" mb={4}>Primary Keyword</Text>
                <Badge variant="outline" size="lg">{contentItem.primaryKeyword}</Badge>
                {contentItem.keywordSearchVolume != null && (
                  <Group gap="xs" mt="xs">
                    <Badge size="sm" variant="light" color="blue">
                      {contentItem.keywordSearchVolume.toLocaleString()}/mo
                    </Badge>
                    {contentItem.keywordDifficulty != null && (
                      <Badge size="sm" variant="light" color={contentItem.keywordDifficulty < 30 ? "green" : contentItem.keywordDifficulty < 60 ? "yellow" : "red"}>
                        KD {Math.round(contentItem.keywordDifficulty)}
                      </Badge>
                    )}
                  </Group>
                )}
              </div>

              <Group grow>
                <div>
                  <Text size="sm" c="dimmed" mb={4}>Target Word Count</Text>
                  <Text fw={500}>{contentItem.targetWordCount || "—"}</Text>
                </div>
                <div>
                  <Text size="sm" c="dimmed" mb={4}>Content Type</Text>
                  <Text fw={500}>{contentItem.contentType}</Text>
                </div>
              </Group>

              <Paper p="md" withBorder>
                <Stack gap="sm">
                  <Group>
                    <IconCalendar size={16} />
                    <Text fw={600}>Schedule Publication</Text>
                  </Group>
                  <DatePicker value={editData.scheduledDate} onChange={handleScheduleChange} placeholder="Select publication date" clearable />
                  {contentItem.scheduledDate && (
                    <Text size="sm" c="dimmed">
                      Currently scheduled for: {new Date(contentItem.scheduledDate).toLocaleDateString()}
                    </Text>
                  )}
                </Stack>
              </Paper>

              <Button variant="light" onClick={() => setIsEditing(true)}>Edit Details</Button>
            </Stack>
          )}
        </Tabs.Panel>

        {/* ========== BRIEF TAB ========== */}
        {isPreGeneration && (
          <Tabs.Panel value="brief">
            <Stack gap="md">
              <Paper p="md" withBorder>
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Text fw={600} size="sm">Optional notes</Text>
                    <Button variant="subtle" size="xs" leftSection={<IconSparkles size={14} />} onClick={handleGenerateBrief} loading={isGeneratingBrief}>
                      {brief ? "Rewrite notes" : "Draft notes"}
                    </Button>
                  </Group>
                  <Text size="xs" c="dimmed">
                    Articles are written from this cluster&apos;s research. Add notes only if you want extra instructions — not a prerequisite.
                  </Text>
                  <Textarea
                    placeholder="Extra instructions: tone, things to include or leave out, CTAs..."
                    value={brief}
                    onChange={(e) => setBrief(e.target.value)}
                    onBlur={handleSaveBrief}
                    minRows={6}
                    maxRows={20}
                    autosize
                  />
                </Stack>
              </Paper>

              <Paper p="md" withBorder>
                <Stack gap="sm">
                  <Text fw={600} size="sm">Generation Notes</Text>
                  <Textarea
                    placeholder="Add notes or instructions for the AI content generator..."
                    value={generationNotes}
                    onChange={(e) => setGenerationNotes(e.target.value)}
                    onBlur={handleSaveGenerationNotes}
                    minRows={3}
                    autosize
                  />
                  <Text size="xs" c="dimmed">
                    These notes will be included as instructions when content is generated. Saved automatically.
                  </Text>
                </Stack>
              </Paper>
            </Stack>
          </Tabs.Panel>
        )}
      </Tabs>
    </Drawer>
  );
}
